import { useState, useRef, useEffect, useCallback } from "react";
import { Info, ExternalLink, Play } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";

function getVideoEmbed(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/);
  if (yt) return { type: "iframe", src: `https://www.youtube.com/embed/${yt[1]}` };
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return { type: "iframe", src: `https://player.vimeo.com/video/${vimeo[1]}` };
  const loom = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
  if (loom) return { type: "iframe", src: `https://www.loom.com/embed/${loom[1]}` };
  if (url.match(/\.(mp4|webm|mov)(\?.*)?$/i)) return { type: "video", src: url };
  return { type: "link", src: url };
}

export function InfoPopover({
  title,
  descriptionEn,
  descriptionDe,
  detailsEn,
  detailsDe,
  videoUrl,
  language = "en",
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const isTouch = useRef(false);

  const desc = language === "en"
    ? (descriptionEn || descriptionDe)
    : (descriptionDe || descriptionEn);
  const details = language === "en"
    ? (detailsEn || detailsDe)
    : (detailsDe || detailsEn);

  useEffect(() => {
    return () => clearTimeout(closeTimer.current);
  }, []);

  const hasContent = desc || details || videoUrl;
  if (!hasContent) return null;

  const embed = getVideoEmbed(videoUrl);

  const cancelClose = () => clearTimeout(closeTimer.current);
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 300);
  };

  const onPointerEnterTrigger = () => {
    if (isTouch.current) return;
    cancelClose();
    setOpen(true);
  };
  const onPointerLeaveTrigger = () => {
    if (isTouch.current) return;
    scheduleClose();
  };
  const onPointerEnterContent = () => {
    if (isTouch.current) return;
    cancelClose();
  };
  const onPointerLeaveContent = () => {
    if (isTouch.current) return;
    scheduleClose();
  };

  return (
    <Popover open={open} onOpenChange={(v) => { cancelClose(); setOpen(v); }}>
      <div
        onMouseEnter={onPointerEnterTrigger}
        onMouseLeave={onPointerLeaveTrigger}
        onTouchStart={() => { isTouch.current = true; }}
        className="inline-flex"
      >
        <PopoverTrigger asChild>
          <button
            className={`inline-flex items-center justify-center w-[18px] h-[18px] rounded-full bg-white/[0.06] hover:bg-[#C9A96E]/20 text-white/35 hover:text-[#C9A96E] transition-all duration-200 shrink-0 cursor-help ${className}`}
            onClick={(e) => e.stopPropagation()}
            data-testid="info-popover-trigger"
          >
            <Info className="w-3 h-3" strokeWidth={2.5} />
          </button>
        </PopoverTrigger>
      </div>
      <PopoverContent
        side="top"
        align="center"
        sideOffset={8}
        onMouseEnter={onPointerEnterContent}
        onMouseLeave={onPointerLeaveContent}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="w-80 max-w-[92vw] p-0 rounded-xl border border-white/[0.08] shadow-[0_8px_40px_-8px_rgba(0,0,0,0.85)] z-[100]"
        style={{ backgroundColor: "#111318" }}
        data-testid="info-popover-content"
      >
        <div className="p-4 space-y-2.5">
          {title && (
            <h4 className="text-white font-semibold text-[13px] leading-snug tracking-wide" data-testid="info-popover-title">
              {title}
            </h4>
          )}
          {desc && (
            <p className="text-[#c4c4cc] text-[13px] leading-[1.6]" data-testid="info-popover-description">
              {desc}
            </p>
          )}
          {details && (
            <div className="pt-1.5 mt-1 border-t border-white/[0.06]">
              <p className="text-white/45 text-xs leading-[1.65]" data-testid="info-popover-details">
                {details}
              </p>
            </div>
          )}
          {embed && embed.type === "iframe" && (
            <div className="pt-1.5 rounded-lg overflow-hidden aspect-video bg-black/50" data-testid="info-popover-video">
              <iframe
                src={embed.src}
                className="w-full h-full rounded-lg"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={title || "Video"}
              />
            </div>
          )}
          {embed && embed.type === "video" && (
            <div className="pt-1.5 rounded-lg overflow-hidden bg-black/50" data-testid="info-popover-video">
              <video
                src={embed.src}
                controls
                className="w-full rounded-lg"
                preload="metadata"
              />
            </div>
          )}
          {embed && embed.type === "link" && (
            <a
              href={videoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 flex items-center gap-2 px-3 py-2 bg-[#C9A96E]/10 hover:bg-[#C9A96E]/20 rounded-lg text-[#C9A96E] text-[13px] transition-colors"
              onClick={(e) => e.stopPropagation()}
              data-testid="info-popover-video-link"
            >
              <Play className="w-4 h-4 shrink-0" />
              <span className="font-medium">
                {language === "en" ? "Watch Video" : "Video ansehen"}
              </span>
              <ExternalLink className="w-3 h-3 ml-auto opacity-50" />
            </a>
          )}
        </div>
        <div className="h-[2px] w-full bg-gradient-to-r from-[#C9A96E]/40 via-[#C9A96E]/25 to-transparent rounded-b-xl" />
      </PopoverContent>
    </Popover>
  );
}
